import { formatPrice } from 'utils';
import {
  AddExtraFeeModal,
  ButtonCommon,
  ConfirmationModal,
  QuickViewTransactionsModal,
  showAlertNotification,
} from '..';
import { Bank, GlobeHemisphereEast, Info, Money, PaperPlaneTilt, Plus, X } from '@phosphor-icons/react';
import { Printer } from '@phosphor-icons/react/dist/ssr';
import { useGetSaleOrderByIdQuery } from 'services';
import { SaleOrder, SaleOrderStatusCode } from 'models/SaleOrder';
import React from 'react';
import { useCart } from 'stores/cartPOSContext';
import { PaymentType } from 'models';
import { DebtDetailModal } from 'components/Debt';
import { useCreateSaleOrderBillMutation } from 'services/sale.service';
import { useUpdateCancelSaleOrderMutation } from 'services/cancelSaleOrder.service';
import { useNavigate } from 'react-router-dom';
import { ETransType } from 'models/Transaction';
import envService, { HiddenFeatures } from 'services/env.service';

const SaleOrderPaymentInfo: React.FC<{ saleOrderId: string; onFinish?: () => void }> = ({ saleOrderId }) => {
  const { getActiveCart, clearCart } = useCart();
  const activeCart = getActiveCart();
  const [createSaleOrderBill, { isLoading }] = useCreateSaleOrderBillMutation();

  const { data: saleOrderData } = useGetSaleOrderByIdQuery({
    id: saleOrderId,
    query: {
      include: 'saleOrderItems|status|customer.baseUser',
      includeRef: 'Transactions.transactionMethod|Debt',
    },
  });
  const [updateCancelSaleOrder] = useUpdateCancelSaleOrderMutation();
  const [saleOrder, setSaleOrder] = React.useState<SaleOrder | null>(null);
  const [isPaymentHistoryModalOpen, setIsPaymentHistoryModalOpen] = React.useState<boolean>(false);
  const [isExtraFeeModalOpen, setIsExtraFeeModalOpen] = React.useState<boolean>(false);
  const [discountVale, setDiscountValue] = React.useState<number>(0);
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isModalOpenCancel, setIsModalOpenCancel] = React.useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState(false);
  const rawTotal = activeCart?.totalAmount || 0;
  const navigate = useNavigate();
  React.useEffect(() => {
    if (saleOrderData) {
      setSaleOrder(saleOrderData.data);

      if (saleOrderData?.data.discountUnit === 'PERCENT') {
        setDiscountValue(
          (rawTotal * (1 + (saleOrderData?.data.vat || 0) / 100) * (saleOrderData?.data.discount || 0)) / 100,
        );
      } else {
        setDiscountValue(saleOrderData?.data.discount || 0);
      }
    }
  }, [saleOrderData]);
  const depositAmount =
    saleOrder?.Transactions?.reduce(
      (total, transaction) =>
        total + (transaction.type === ETransType.Deposit ? Number(transaction.transactionAmount) : 0),
      0,
    ) || 0;
  const extraFeeAmount =
    saleOrder?.Transactions?.reduce(
      (total, transaction) =>
        total + (transaction.type === ETransType.OS_EXTRA_FEE ? Number(transaction.transactionAmount) : 0),
      0,
    ) || 0;
  const paidAmount =
    saleOrder?.paymentType === PaymentType.FULL_PAYMENT
      ? saleOrder?.total || 0
      : saleOrder?.status?.code === SaleOrderStatusCode.DRAFT
        ? saleOrder?.deposit || 0
        : depositAmount;

  const handlePrintBills = async (orderId: string) => {
    try {
      const response = await createSaleOrderBill({ id: orderId });
      const billUrl = response?.data?.data?.fileUrl;
      if (!billUrl) return;

      const res = await fetch(billUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      };
    } catch (error) {
      console.log(error);
      if ((error as Error)?.message) {
        alert((error as Error)?.message);
      } else {
        alert('Lỗi in hóa đơn');
      }
    }
  };
  const handleExportInvoice = async () => {
    try {
      if (saleOrder) {
        clearCart();
        navigate(`/invoices/electronic-invoices/create-invoice`, {
          state: { saleOrder: saleOrder },
        });
      }
    } catch (error) {
      console.log(error);
      if ((error as Error)?.message) {
        alert((error as Error)?.message);
      } else {
        alert('Lỗi xuất hóa đơn điện tử');
      }
    }
  };

  return (
    <div className='flex flex-col gap-4 p-4 bg-white rounded-lg h-full'>
      <div className='text-lg font-bold text-[var(--textBlack100)]'>THÔNG TIN THANH TOÁN</div>
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4 gap-y-4 items-center'>
        <div className='text-sm text-[var(--textBlack100)]'>Số lượng</div>
        <div className='text-sm font-medium flex items-center gap-2 md:col-span-3'>
          <span className='flex gap-2 text-[var(--textBlack56)]'>
            <span className='font-medium text-[var(--textBlack100)]'>
              {saleOrder?.saleOrderItems?.reduce((total, item) => total + item.quantity, 0)}
            </span>{' '}
            sản phẩm
          </span>
        </div>

        <span className='text-sm text-[var(--textBlack100)]'>Tạm tính</span>
        <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
          {formatPrice(activeCart?.totalAmount || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
        </span>

        <hr className='my-2 md:col-span-4' />

        <span className='text-sm text-[var(--textBlack100)]'>VAT</span>
        <span className='md:col-span-3 font-medium text-[var(--textBlack100)]  flex items-center'>
          {saleOrder?.vat || 0}% <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
          {formatPrice((rawTotal * (saleOrder?.vat || 0)) / 100)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>
        <span className='text-sm text-[var(--textBlack100)]'>Giảm giá hạng thẻ</span>
        <span className='md:col-span-3 font-medium text-[var(--textBlack100)]  flex items-center'>
          {saleOrder?.memberDiscount || 0} % <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
          {formatPrice((rawTotal * (saleOrder?.memberDiscount || 0)) / 100)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>
        <span className='text-sm text-[var(--textBlack100)]'>Giảm giá</span>
        <span className='md:col-span-3 font-medium text-[var(--textBlack100)]  flex items-center'>
          {saleOrder?.discountUnit === 'PERCENT' && (
            <>
              {saleOrder?.discount || 0} {saleOrder?.discountUnit === 'PERCENT' ? '%' : 'VND'}{' '}
              <span className='text-[var(--textBlack56)] mx-2'>~</span>{' '}
            </>
          )}
          {formatPrice(discountVale)}
          <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
        </span>
        <span className='text-sm text-[var(--textBlack100)]'>Phụ phí</span>
        <span className='col-span-3 font-medium text-[var(--textBlack100)]   flex items-center justify-between'>
          <span>
            {formatPrice(extraFeeAmount)}
            <span className='ml-2 text-[var(--textBlack56)]'> VND</span>
          </span>
          <ButtonCommon
            size='xxs'
            color='primary'
            radius='rounded'
            onClick={() => setIsExtraFeeModalOpen(true)}
            className='!px-2'
            icon={<Plus size={14} />}
          >
            Thêm phụ phí
          </ButtonCommon>
        </span>
        <hr className='my-2 md:col-span-4' />
        <span className='text-sm font-bold text-[var(--textBlack100)]'>Tổng tiền</span>
        <span className='md:col-span-3 font-bold text-[var(--textBlack100)]'>
          {formatPrice(saleOrder?.total || 0)} <span className='text-[var(--textBlack56)]'>VND</span>
        </span>
        <span className='text-sm font-bold text-[var(--textBlack100)]'>Đã thanh toán</span>
        <span className='md:col-span-3 flex justify-between font-bold text-[var(--primaryColor500)]'>
          <span>
            {saleOrder?.paymentType === PaymentType.FULL_PAYMENT
              ? formatPrice(saleOrder?.total || 0)
              : formatPrice(paidAmount || 0)}{' '}
            <span className='opacity-60'>VND</span>
          </span>
          <span className='ml-2 font-normal flex items-center'>
            {saleOrder?.Transactions?.[0]?.transactionMethod?.code === 'CASH' ? (
              <>
                <Money size={20} weight='fill' /> <span className='ml-1'>Tiền mặt</span>
              </>
            ) : saleOrder?.Transactions?.[0]?.transactionMethod?.code === 'BANK_TRANSFER' ? (
              <>
                <Bank size={20} weight='fill' /> <span className='ml-1'>Chuyển khoản</span>
              </>
            ) : null}
          </span>
        </span>
        <span className='text-sm text-[var(--textBlack56)]'>Còn lại</span>
        <span className='col-span-3 font-bold text-[var(--textBlack100)]'>
          {saleOrder?.paymentType === PaymentType.FULL_PAYMENT
            ? formatPrice(0)
            : formatPrice(Number(saleOrder?.total || 0) - Number(paidAmount || 0))}{' '}
          <span className='text-[var(--textBlack56)]'>VND</span>
        </span>
        <div
          className='md:col-span-4 flex items-center gap-2 cursor-pointer '
          onClick={() => setIsPaymentHistoryModalOpen(true)}
        >
          <div className='text-sm text-[var(--primaryColor500)] font-bold'>Lịch sử thanh toán</div>
        </div>
      </div>
      <div className='flex flex-col gap-2 mt-4'>
        {saleOrder?.paymentType !== PaymentType.FULL_PAYMENT &&
          saleOrder?.status?.code !== SaleOrderStatusCode.DRAFT && (
            <ButtonCommon
              color='dangerSoft'
              onClick={() => setIsDetailModalOpen(true)}
              radius='rounded'
              icon={<Info size={20} />}
            >
              Chi tiết công nợ
            </ButtonCommon>
          )}
        {saleOrder?.status?.code !== SaleOrderStatusCode.DRAFT && (
          <ButtonCommon
            onClick={() => handlePrintBills(saleOrder?.id || '')}
            color='soft'
            radius='rounded'
            icon={<Printer size={20} />}
            loading={isLoading}
          >
            In hóa đơn
          </ButtonCommon>
        )}
        {saleOrder?.status?.code === SaleOrderStatusCode.DRAFT && (
          <ButtonCommon
            color='dangerSoft'
            onClick={() => {
              navigate(`/management/sale-orders/detail?id=${saleOrderId}`);
            }}
            radius='rounded'
            icon={<GlobeHemisphereEast size={20} />}
          >
            Chi tiết đơn hàng
          </ButtonCommon>
        )}
        {!envService.isFeatureHidden(HiddenFeatures.EInvoice) && (
          <ButtonCommon
            color='soft'
            radius='rounded'
            icon={<PaperPlaneTilt size={20} />}
            onClick={() => handleExportInvoice()}
            className='w-full'
          >
            Tạo hoá đơn điện tử
          </ButtonCommon>
        )}
        <ButtonCommon
          color='primary'
          onClick={() => {
            setIsModalOpen(true);
          }}
          radius='rounded'
          icon={<X size={20} />}
        >
          Đóng đơn hàng
        </ButtonCommon>
        <ButtonCommon
          color='danger'
          onClick={() => {
            setIsModalOpenCancel(true);
          }}
          radius='rounded'
          icon={<X size={20} />}
        >
          Hủy đơn hàng
        </ButtonCommon>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        title='Xác nhận đóng đơn hàng'
        subtitle='Bạn có chắc chắn muốn đóng đơn hàng này?'
        confirmText='Đóng đơn hàng'
        cancelText='Hủy'
        onConfirm={() => {
          clearCart();
          setIsModalOpen(false);
        }}
      />
      <ConfirmationModal
        isOpen={isModalOpenCancel}
        onCancel={() => {
          setIsModalOpenCancel(false);
        }}
        title='Xác nhận hủy đơn hàng'
        subtitle='Bạn có chắc chắn muốn hủy đơn hàng này?'
        confirmText='Hủy đơn hàng'
        cancelText='Quay lại'
        onConfirm={async () => {
          try {
            const res = await updateCancelSaleOrder({ id: saleOrderId }).unwrap();
            showAlertNotification({
              type: 'success',
              title: 'Hủy đơn hàng thành công',
              message: 'đơn hàng đã được hủy',
            });
            if (res) {
              clearCart();
              navigate(`/management/sale-orders/detail?id=${saleOrderId}`);
            }
          } catch (error) {
            console.log(error);
            showAlertNotification({
              type: 'error',
              title: 'Hủy đơn hàng thất bại',
              message: 'Hủy đơn hàng thất bại!',
            });
          } finally {
            setIsModalOpenCancel(false);
          }
        }}
        icon='delete'
      />
      <DebtDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        debtId={saleOrder?.Debt?.[0]?.id || null}
      />
      <QuickViewTransactionsModal
        isOpen={isPaymentHistoryModalOpen}
        onClose={() => setIsPaymentHistoryModalOpen(false)}
        transactions={saleOrder?.Transactions || []}
      />
      <AddExtraFeeModal
        saleOrderId={saleOrder?.id || ''}
        isOpen={isExtraFeeModalOpen}
        onCancel={() => setIsExtraFeeModalOpen(false)}
      />
    </div>
  );
};
export default SaleOrderPaymentInfo;
